import React from "react";

// Small drawn previews of each template for the style menu. Drawings, not
// anyone's photos, so every option is easy to picture before choosing. All nine
// share one 120 x 84 shape, so the menu stays an even grid.

const SKY = "fill-[#bcd4e2]";
const SKY2 = "fill-[#cfe0e8]";
const HILL = "fill-[#86a877]";
const HILL2 = "fill-[#6e9466]";
const SEPIA = "fill-[#cdb892]";
const SEPIA2 = "fill-[#a38e68]";
const PRINT = "fill-[#fffdf6] stroke-black/10";
const CARD = "fill-dupontCream stroke-dupontGoldLt/50 dark:fill-neutral-800 dark:stroke-neutral-700";
const LINE = "fill-dupontGoldLt/40 dark:fill-neutral-600";
const INK = "fill-dupontBlue/80 dark:fill-gray-300";
const GOLD = "fill-dupontGold";

function Frame({ children, wall = false }) {
  return (
    <svg viewBox="0 0 120 84" className="block w-full h-auto rounded-[7px]" aria-hidden="true">
      <rect x="1" y="1" width="118" height="82" rx="7" strokeWidth="1"
            className={wall ? "fill-dupontCreamDk stroke-dupontGoldLt/50 dark:fill-neutral-800" : CARD} />
      {children}
    </svg>
  );
}

export function Snapshot() {
  return (
    <Frame>
      <rect className={INK} x="16" y="8" width="30" height="4" rx="2" />
      <rect className={GOLD} x="84" y="8" width="20" height="3" rx="1.5" />
      <rect className={SKY} x="16" y="17" width="88" height="52" />
      <ellipse className={HILL} cx="42" cy="69" rx="44" ry="18" />
      <ellipse className={HILL2} cx="90" cy="71" rx="36" ry="14" />
      <circle className="fill-[#fbf3d5]" cx="88" cy="29" r="5" />
      <rect className={LINE} x="16" y="74" width="46" height="3" rx="1.5" />
    </Frame>
  );
}

export function Keepsake() {
  return (
    <Frame>
      <g transform="rotate(-5 60 42)">
        <rect className={PRINT} strokeWidth="0.8" x="38" y="7" width="44" height="66" rx="1" />
        <rect className={SKY} x="42" y="11" width="36" height="42" />
        <ellipse className={HILL} cx="60" cy="53" rx="24" ry="10" />
        <rect className="fill-black/15" x="46" y="60" width="28" height="3" rx="1.5" />
        <rect className="fill-black/15" x="50" y="66" width="20" height="3" rx="1.5" />
      </g>
    </Frame>
  );
}

export function ThenNow() {
  return (
    <Frame>
      <rect className={SEPIA} x="14" y="10" width="44" height="56" />
      <ellipse className={SEPIA2} cx="36" cy="66" rx="26" ry="12" />
      <rect className={SKY} x="62" y="10" width="44" height="56" />
      <ellipse className={HILL} cx="84" cy="66" rx="26" ry="12" />
      <rect className={CARD} x="18" y="57" width="18" height="6" rx="1" />
      <rect className={CARD} x="66" y="57" width="18" height="6" rx="1" />
      <rect className={LINE} x="14" y="72" width="40" height="3" rx="1.5" />
    </Frame>
  );
}

export function Clipping() {
  return (
    <Frame>
      <path className="fill-[#f3ecd6] stroke-dupontGoldLt/50 dark:fill-neutral-700" strokeWidth="1" d="M32 8h46l10 10v58H32z" />
      <path className={LINE} d="M78 8v10h10z" />
      <rect className={INK} x="42" y="18" width="30" height="4" rx="1" />
      {[30, 38, 46].map((y) => <rect key={y} className={LINE} x="40" y={y} width="40" height="3" rx="1.5" />)}
      <rect className={LINE} x="40" y="54" width="28" height="3" rx="1.5" />
    </Frame>
  );
}

export function Album() {
  return (
    <Frame>
      <rect className={SKY} x="14" y="10" width="46" height="46" />
      <ellipse className={HILL} cx="37" cy="56" rx="26" ry="11" />
      <rect className={SKY2} x="64" y="10" width="20" height="21" />
      <rect className={SEPIA} x="88" y="10" width="18" height="21" />
      <rect className={HILL} x="64" y="35" width="20" height="21" />
      <rect className={SKY} x="88" y="35" width="18" height="21" />
      <rect className={LINE} x="14" y="66" width="44" height="3" rx="1.5" />
    </Frame>
  );
}

export function Placard() {
  return (
    <Frame wall>
      <rect className={PRINT} strokeWidth="0.8" x="16" y="10" width="42" height="54" />
      <rect className="fill-[#ece8df]" x="20" y="14" width="34" height="46" />
      <path className="fill-[#aeb2b8]" d="M29 26h16l-3 18h-10z" />
      <rect className="fill-[#aeb2b8]" x="35" y="44" width="4" height="8" />
      <rect className={PRINT} strokeWidth="0.8" x="66" y="38" width="40" height="30" />
      <rect className={GOLD} x="70" y="38" width="32" height="2" />
      <rect className="fill-dupontBlue/80" x="70" y="45" width="24" height="3.5" rx="1" />
      <rect className="fill-black/15" x="70" y="52" width="30" height="2.5" rx="1" />
      <rect className="fill-black/15" x="70" y="58" width="22" height="2.5" rx="1" />
    </Frame>
  );
}

export function DidYouKnow() {
  return (
    <Frame>
      <text x="84" y="66" className="fill-dupontGold/30 font-display italic" fontSize="52">?</text>
      <circle className="fill-white stroke-dupontGold dark:fill-neutral-900" strokeWidth="1" cx="32" cy="32" r="17" />
      <circle className={SKY} cx="32" cy="32" r="15" />
      <path className={HILL} d="M17 36c6-4 12-4 18 0s10 3 12 1v1a15 15 0 0 1-30 0z" />
      <rect className={GOLD} x="56" y="16" width="26" height="3" rx="1.5" />
      <rect className={INK} x="56" y="24" width="48" height="4" rx="1" />
      <rect className={INK} x="56" y="32" width="38" height="4" rx="1" />
      <rect className={LINE} x="14" y="62" width="40" height="3" rx="1.5" />
    </Frame>
  );
}

export function PopUpBook() {
  return (
    <Frame>
      <rect className={PRINT} strokeWidth="0.8" x="20" y="16" width="44" height="34" transform="rotate(-6 42 33)" />
      <rect className={PRINT} strokeWidth="0.8" x="22" y="16" width="44" height="34" transform="rotate(3 44 33)" />
      <rect className={SKY} x="21" y="15" width="44" height="34" />
      <polygon className="fill-[#f6f1e4]" points="30,45 43,26 56,45" />
      <rect className={INK} x="74" y="22" width="32" height="4" rx="1" />
      <rect className={LINE} x="74" y="31" width="24" height="3" rx="1.5" />
      <circle className={GOLD} cx="76" cy="44" r="2" />
      {[83, 90, 97].map((cx) => <circle key={cx} className={LINE} cx={cx} cy="44" r="2" />)}
      <rect className={LINE} x="20" y="62" width="44" height="3" rx="1.5" />
    </Frame>
  );
}

export function Generations() {
  const fills = [SEPIA, SEPIA2, SKY2, SKY];
  return (
    <Frame>
      <rect className={GOLD} x="22" y="33" width="76" height="1.2" />
      {[22, 47, 72, 97].map((cx, i) => (
        <g key={cx}>
          <ellipse className="fill-white stroke-dupontGold dark:fill-neutral-900" strokeWidth="1" cx={cx} cy="33" rx="11" ry="14" />
          <ellipse className={fills[i]} cx={cx} cy="33" rx="9.5" ry="12.5" />
          <rect className={LINE} x={cx - 8} y="54" width="16" height="3" rx="1.5" />
        </g>
      ))}
      <rect className={LINE} x="34" y="68" width="52" height="3" rx="1.5" />
    </Frame>
  );
}
